// Batched Duo/Trio multi-album customer flow.
//
// Spec: docs/superpowers/specs/2026-06-04-multi-album-flow-design.md
//
// STRATEGY (single-turn scope-conscious implementation)
// The root of the 100% drop-off is that Legacy hides album 2 until the
// customer has finished album 1's entire 7-step pipeline. V2 fixes that by
// surfacing the FULL journey shape up front: progress bar + cover picker
// for EVERY album in sequence. Once the customer has committed a cover for
// each album they hand off to the legacy pipeline (which already handles
// upload, AI, manual edit, preview, confirm, submit per-album via its own
// session resume) via `?force_legacy=1`. The progress bar told them this
// was coming, so it no longer ambushes them.
//
// Reuses: StepDesign cover picker, coversApi, sessionsApi (all from Legacy).
// New here: per-album cover state machine, progress orchestration, resume.
//
// What's NOT done in this iteration (deferred for the next implementation pass):
//   - Phase 2 mode-choice screen and AI parallel batch (Steps 4 of the plan).
//     Customers go straight from "covers picked" to Legacy, which then runs
//     its per-album pipeline for the rest. Legacy still works the same — the
//     win here is the upfront awareness.
//   - In-flow upload UI (Legacy renders it). Decided pragmatic: Legacy's
//     StepUpload is battle-tested, no reason to fork it before we know if
//     the cover-upfront fix moves the conversion needle.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  coversApi,
  coverPinsApi,
  sessionsApi,
  ordersApi,
  type Cover,
} from '@/lib/api';
import { MultiAlbumProgress } from '@/components/customer/MultiAlbumProgress';
import { AlbumPhaseHeader } from '@/components/customer/AlbumPhaseHeader';
import { StepDesign } from './AlbumFlowPage';
import { titlePrefill } from '@/lib/titleSource';

const C = {
  gold:      '#C9A24A',
  goldDeep:  '#A8842F',
  goldLight: '#D4B86A',
  goldDark:  '#8B6F26',
  beige:     '#F5EFE0',
  dark:      '#1F2937',
  muted:     '#6B7280',
  white:     '#FFFFFF',
  success:   '#10B981',
};

// Per-album working state. Mirrors Legacy's per-album fields but keyed by
// albumIndex so we can keep both/all three in memory at once.
interface AlbumDraft {
  cover: Cover | null;
  destination: string;
  /** The words for the cover — the album's own title (lib/titleSource.ts). */
  title: string;
  year: string;
}

export default function AlbumFlowPageV2() {
  const { shopifyOrderId } = useParams<{ shopifyOrderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Order + total albums ──────────────────────────────────────────────────
  const { data: orderData } = useQuery({
    queryKey: ['order', shopifyOrderId],
    queryFn: () => ordersApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });
  const order = orderData?.order ?? null;
  const totalAlbums: number = useMemo(() => {
    const b = order?.bundle ?? 'Solo';
    return b === 'Trio' ? 3 : b === 'Duo' ? 2 : 1;
  }, [order?.bundle]);

  // ── Covers + categories ──────────────────────────────────────────────────
  const { data: coversData } = useQuery({
    queryKey: ['covers'],
    queryFn: () => coversApi.list(),
  });
  // Per-order pinned cover (Task 4 part E) — floated to the front of the picker.
  const { data: coverPinData } = useQuery({
    queryKey: ['cover-pin', shopifyOrderId],
    queryFn: () => coverPinsApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });
  const pinnedCoverId = coverPinData?.cover_id ?? null;
  const coversRaw = coversData?.covers ?? [];
  // Covers arrive popularity-ranked from the worker; move any pinned cover first.
  const covers = useMemo(() => {
    if (!pinnedCoverId) return coversRaw;
    const idx = coversRaw.findIndex((c) => c.id === pinnedCoverId);
    if (idx <= 0) return coversRaw;
    const copy = coversRaw.slice();
    const [pinned] = copy.splice(idx, 1);
    return [pinned, ...copy];
  }, [coversRaw, pinnedCoverId]);
  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories'],
    queryFn: () => coversApi.categories(),
  });
  const categories = categoriesData?.categories ?? [];

  // ── Per-album drafts ──────────────────────────────────────────────────────
  // Hydrated from existing sessions on mount so a returning customer who
  // already picked one cover doesn't redo it.
  const [drafts, setDrafts] = useState<Record<number, AlbumDraft>>({});
  const [activeAlbum, setActiveAlbum] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Initial hydration — read all sessions for this order, populate drafts,
  // jump to the first album that still needs a cover. Implements the spec's
  // "jump to where you left off" resume rule.
  useEffect(() => {
    if (!shopifyOrderId || !order || hydrated) return;
    if (covers.length === 0) return;
    (async () => {
      const init: Record<number, AlbumDraft> = {};
      const destinations = [order.destination_1, order.destination_2, order.destination_3];
      const years        = [order.year_1,        order.year_2,        order.year_3];
      let firstIncomplete = 1;
      for (let i = 1; i <= totalAlbums; i++) {
        try {
          const { session } = await sessionsApi.get(shopifyOrderId, i);
          const savedCover = session?.cover_id
            ? covers.find((c) => c.id === session.cover_id) ?? null
            : null;
          init[i] = {
            cover: savedCover,
            destination: destinations[i - 1] ?? '',
            // The album's title, not the order's copy: after a title change
            // the copy can be stale, and this wrote it back on every pick.
            title: titlePrefill(session, destinations[i - 1]),
            year: years[i - 1] ?? String(new Date().getFullYear()),
          };
          if (!savedCover && firstIncomplete === 1) firstIncomplete = i;
          else if (savedCover && firstIncomplete === i) firstIncomplete = i + 1;
        } catch {
          init[i] = {
            cover: null,
            destination: destinations[i - 1] ?? '',
            title: titlePrefill(null, destinations[i - 1]),
            year: years[i - 1] ?? String(new Date().getFullYear()),
          };
        }
      }
      setDrafts(init);
      setActiveAlbum(Math.min(firstIncomplete, totalAlbums));
      setHydrated(true);
    })();
  }, [shopifyOrderId, order, covers, totalAlbums, hydrated]);

  // ── Cover persist + advance ──────────────────────────────────────────────
  const persistCover = useCallback(async (albumIndex: number, cover: Cover) => {
    if (!shopifyOrderId) return;
    setSaving(true);
    try {
      await sessionsApi.upsert(shopifyOrderId, albumIndex, {
        destination: drafts[albumIndex]?.destination ?? null,
        year: drafts[albumIndex]?.year ?? null,
        cover_id: cover.id,
        cover_destination_text: drafts[albumIndex]?.title || null,
        cover_year_text: drafts[albumIndex]?.year ?? null,
        status: 'draft',
      });
    } finally {
      setSaving(false);
    }
  }, [shopifyOrderId, drafts]);

  const handleSelectCover = useCallback(async (cover: Cover) => {
    setDrafts((p) => ({ ...p, [activeAlbum]: { ...(p[activeAlbum] ?? { destination: '', title: '', year: '' }), cover } }));
    await persistCover(activeAlbum, cover);
  }, [activeAlbum, persistCover]);

  // ── Browser-back handling (deterministic sentinel trap) ───────────────────
  // Same trap as the V1 flow (AlbumFlowPage.tsx): one re-armed history sentinel
  // sits on top while the customer is picking covers. Each back gesture (iOS
  // edge-swipe, Android back, header button via popstate) pops it → we
  // decrement activeAlbum and re-arm. Only album 1 lets the browser exit. This
  // never relies on history DEPTH, so the hydration resume-jump (which sets
  // activeAlbum directly) can't under-flow and escape to the COD form.
  const activeAlbumRef = useRef(activeAlbum);
  useEffect(() => { activeAlbumRef.current = activeAlbum; }, [activeAlbum]);

  const trapArmedRef = useRef(false);
  useEffect(() => {
    if (!trapArmedRef.current) {
      trapArmedRef.current = true;
      try { window.history.pushState({ storizTrap: true }, ''); } catch { /* ignore */ }
    }
    const onPop = () => {
      const current = activeAlbumRef.current;
      if (current <= 1) {
        // First album → allow the real exit (back past the /flow entry).
        try { window.history.back(); } catch { /* ignore */ }
        return;
      }
      setActiveAlbum(current - 1);
      setSelectedCategory(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      try { window.history.pushState({ storizTrap: true }, ''); } catch { /* ignore */ }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Next button: move to the next album, or hand off to Legacy when all
  // covers are committed. `?force_legacy=1` tells AlbumFlowPage's switcher
  // to render Legacy regardless of the flag, with the existing
  // session-resume logic landing the customer at upload/1 (or wherever
  // they left off if they previously got further).
  const handleNext = useCallback(() => {
    if (activeAlbum < totalAlbums) {
      // Forward is pure state — the sentinel trap handles back independently.
      setActiveAlbum(activeAlbum + 1);
      // Reset category filter so each album feels like a fresh decision.
      setSelectedCategory(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // All covers committed → hand off.
    const next = searchParams.get('next');
    const params = new URLSearchParams();
    params.set('force_legacy', '1');
    if (next) params.set('next', next);
    navigate(`/flow/${shopifyOrderId}?${params.toString()}`);
  }, [activeAlbum, totalAlbums, navigate, shopifyOrderId, searchParams]);

  const handleBack = useCallback(() => {
    // State-driven: decrement the album; only album 1 leaves the builder. The
    // sentinel trap handles hardware/edge-swipe back independently.
    if (activeAlbum > 1) {
      setActiveAlbum(activeAlbum - 1);
      setSelectedCategory(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.location.href = 'https://storiz.ma';
    }
  }, [activeAlbum]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (!order || !hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: C.beige }}>
        <div className="text-center">
          <div className="mx-auto w-12 h-12 mb-4 rounded-full border-4 animate-spin" style={{ borderColor: `${C.gold}40`, borderTopColor: C.gold }} />
          <p className="text-sm" style={{ color: C.muted }}>Chargement de vos albums…</p>
        </div>
      </div>
    );
  }

  const activeDraft = drafts[activeAlbum] ?? { cover: null, destination: '', title: '', year: '' };
  const phase1Step = (activeAlbum - 1) * 2 + 1; // we're on the cover sub-step for the active album
  const isAllChosen = Array.from({ length: totalAlbums }).every((_, i) => drafts[i + 1]?.cover);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: C.beige, color: C.dark, minHeight: '100dvh', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <MultiAlbumProgress
        totalAlbums={totalAlbums}
        currentPhase={1}
        phase1Step={phase1Step}
        subLabel={`Couverture · ${activeDraft.destination || `Album ${activeAlbum}`}`}
      />

      <AlbumPhaseHeader
        action="Couverture"
        destination={activeDraft.destination || undefined}
        albumIndex={activeAlbum}
        totalAlbums={totalAlbums}
        hint={`Choisissez le design qui ouvrira votre album ${activeAlbum}${activeDraft.destination ? ` (${activeDraft.destination})` : ''}. Vous pourrez le personnaliser ensuite.`}
      />

      <main className="flex-1 pb-32">
        <StepDesign
          covers={covers}
          categories={categories}
          selectedCover={activeDraft.cover}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          onSelectCover={handleSelectCover}
          destination={activeDraft.destination}
          year={activeDraft.year}
          shopifyOrderId={shopifyOrderId}
          albumIndex={activeAlbum}
          customerName={order?.customer_name ?? null}
          customerPhone={order?.customer_phone ?? null}
        />
      </main>

      {/* Fixed bottom CTA — gold pill. Disabled until the active album has a
          cover. Wording adapts: "Album suivant →" for in-between albums,
          "Commencer mes albums" for the last one (hand-off to Legacy). */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 px-4 pt-4 pb-6 pointer-events-none"
        style={{ background: `linear-gradient(to top, ${C.beige} 75%, transparent)` }}
      >
        <div className="max-w-5xl mx-auto pointer-events-auto flex items-center gap-2">
          {activeAlbum > 1 && (
            <button
              onClick={handleBack}
              className="h-12 px-4 rounded-2xl text-sm font-semibold transition-all"
              style={{ background: `${C.gold}14`, color: C.goldDeep, border: `1px solid ${C.gold}30` }}
            >
              ← Précédent
            </button>
          )}
          <button
            disabled={!activeDraft.cover || saving}
            onClick={handleNext}
            className="flex-1 h-12 rounded-2xl text-base font-bold tracking-wide transition-all disabled:opacity-40"
            style={{
              background: activeDraft.cover && !saving
                ? `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`
                : `${C.gold}40`,
              color: C.white,
              boxShadow: activeDraft.cover && !saving ? `0 6px 20px ${C.gold}55` : 'none',
            }}
          >
            {saving
              ? 'Enregistrement…'
              : activeAlbum < totalAlbums
                ? `Album ${activeAlbum + 1} →`
                : isAllChosen
                  ? 'Continuer vers mes photos →'
                  : 'Choisissez une couverture'}
          </button>
        </div>
      </div>
    </div>
  );
}
